
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Globe, MapPin } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface SiteMetadata {
  title: string;
  description: string;
  keywords: string;
  socialMedia: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
  };
  contact: {
    email?: string;
    phone?: string;
    address?: string;
  };
  googleBusiness?: string;
  mapLocation?: { lat: number; lng: number };
  lastUpdated?: string;
}

interface SiteMetadataProps {
  domain: string;
  metadata: SiteMetadata | null;
  showMetadataProgress: boolean;
  metadataProgress: number;
  isLoading: boolean;
  formatDateTime: (dateString: string) => string;
  fetchSiteMetadata: () => Promise<void>;
}

export const SiteMetadataSection: React.FC<SiteMetadataProps> = ({
  domain,
  metadata,
  showMetadataProgress,
  metadataProgress,
  isLoading,
  formatDateTime,
  fetchSiteMetadata
}) => {
  const { t } = useTranslation();

  return (
    <Card className="p-6">
      <CardHeader className="p-0 mb-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle>{t('analysis.siteInfo')}</CardTitle>
          {metadata?.lastUpdated && (
            <CardDescription>
              {t('analysis.lastUpdated')} {formatDateTime(metadata.lastUpdated)}
            </CardDescription>
          )}
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchSiteMetadata}
          disabled={showMetadataProgress}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${showMetadataProgress ? 'animate-spin' : ''}`} />
          {showMetadataProgress ? t('analysis.refreshing') : t('analysis.updateInfo')}
        </Button>
      </CardHeader>
      <CardContent className="p-0 space-y-6">
        {showMetadataProgress ? (
          <div className="space-y-4 py-8">
            <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300" 
                style={{ width: `${metadataProgress}%` }}
              ></div>
            </div>
            <p className="text-center text-sm text-muted-foreground">
              {t('analysis.scanning', { progress: metadataProgress })}
            </p>
          </div>
        ) : isLoading || !metadata ? (
          <div className="text-center p-4">{t('analysis.loading')}</div>
        ) : (
          <>
            <div className="space-y-3">
              <div className="flex items-start">
                <Globe className="h-5 w-5 mr-2 text-primary" />
                <div>
                  <h3 className="font-medium">{t('analysis.siteTitle')}</h3>
                  <p>{metadata?.title || t('analysis.notFound')}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="h-5 w-5 mr-2" />
                <div>
                  <h3 className="font-medium">{t('analysis.description')}</h3>
                  <p className="text-sm text-muted-foreground">{metadata?.description || t('analysis.notFound')}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="h-5 w-5 mr-2" />
                <div>
                  <h3 className="font-medium">{t('analysis.keywords')}</h3>
                  <p className="text-sm text-muted-foreground">{metadata?.keywords || t('analysis.notFound')}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h3 className="font-semibold">{t('analysis.socialMedia')}</h3>
              {Object.entries(metadata?.socialMedia || {}).length > 0 ? (
                <div className="space-y-2">
                  {metadata?.socialMedia.facebook && (
                    <a href={metadata.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline">
                      <div className="h-4 w-4 mr-2" />Facebook
                    </a>
                  )}
                  {metadata?.socialMedia.twitter && (
                    <a href={metadata.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-400 hover:underline">
                      <div className="h-4 w-4 mr-2" />Twitter
                    </a>
                  )}
                  {metadata?.socialMedia.instagram && (
                    <a href={metadata.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center text-pink-600 hover:underline">
                      <div className="h-4 w-4 mr-2" />Instagram
                    </a>
                  )}
                  {metadata?.socialMedia.linkedin && (
                    <a href={metadata.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-700 hover:underline">
                      <div className="h-4 w-4 mr-2" />LinkedIn
                    </a>
                  )}
                  {metadata?.socialMedia.youtube && (
                    <a href={metadata.socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center text-red-600 hover:underline">
                      <div className="h-4 w-4 mr-2" />YouTube
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('analysis.notFound')}</p>
              )}
            </div>
            
            <div className="space-y-3">
              <h3 className="font-semibold">{t('analysis.contactInfo')}</h3>
              <div className="space-y-2">
                {metadata?.contact.email && (
                  <div className="flex items-center">
                    <div className="h-4 w-4 mr-2" />
                    <span>{t('analysis.email')}</span>
                    <a href={`mailto:${metadata.contact.email}`} className="ml-1 text-blue-600 hover:underline">{metadata.contact.email}</a>
                  </div>
                )}
                
                {metadata?.contact.phone && (
                  <div className="flex items-center">
                    <div className="h-4 w-4 mr-2" />
                    <span>{t('analysis.phone')}</span>
                    <a href={`tel:${metadata.contact.phone}`} className="ml-1 text-blue-600 hover:underline">{metadata.contact.phone}</a>
                  </div>
                )}
                
                {metadata?.contact.address && (
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 mr-2 mt-1 flex-shrink-0" />
                    <span>{t('analysis.address')} {metadata.contact.address}</span>
                  </div>
                )}
              </div>
            </div>
            
            {metadata?.googleBusiness && (
              <div className="space-y-2">
                <h3 className="font-semibold">Google Business</h3>
                <a href={metadata.googleBusiness} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                  <div className="h-4 w-4 mr-2" />
                  {t('analysis.viewGoogleBusiness')}
                </a>
              </div>
            )}
            
            {metadata?.mapLocation && (
              <div className="space-y-2">
                <h3 className="font-semibold">{t('analysis.mapLocation')}</h3>
                <div className="h-48 bg-gray-100 rounded-lg overflow-hidden">
                  <iframe
                    title="Google Maps"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${metadata.mapLocation.lat},${metadata.mapLocation.lng}&zoom=15`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen={false}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
